import { Ref, computed, ref, UnwrapNestedRefs } from 'vue'
import { approvalsApi } from '@/api'
import { useI18n } from 'vue-i18n'
import useToasts from './useToasts'
import useApprovalHistory from './useApprovalHistory'
import usePhrases from './usePhrases'
import useDocuments from './useDocuments'
import { ActivityActions } from '@/composables/useActivityLogs'
import { CanonicalsConfigObject, PhrasePointObject, PropItems } from '@coac-gmbh/saifty-phrases'
import { applyAIRulesToTheConfidenceField, SDS_CLASSIFICATION_CANONICAL_LIST } from '@/config'
import {
  CanonicalAction,
  DataObjectData,
  DataObjectDTO,
  DataObjectStatus,
  DataPointLocation,
  Props,
  Usages,
} from '@coac-gmbh/saifty-main-apis'
import { getCanonicalNameWithoutNumberSuffix } from '@/composables/useValidationItem'
import useRolesAndPermissions from '@/composables/useRolesAndPermissions'
import useDataObjects from '@/composables/useDataObjects'

export const ExtendedDataObjectStatus = {
  ...DataObjectStatus,
  HavingExternalFileId: 'havingExternalFileId',
  NotHavingExternalFileId: 'notHavingExternalFileId',
} as const

export type ExtendedDataObjectStatus = (typeof ExtendedDataObjectStatus)[keyof typeof ExtendedDataObjectStatus]

interface ToggleConfig {
  dataObject: DataObjectDTO
  item: DataObjectData
  cardValue: string
  phrases: PhrasePointObject[]
  newLocation?: DataPointLocation
  isSapMode?: boolean
}

export interface ToggleItemStatusConfig {
  dataObject: DataObjectDTO
  item: DataObjectData
  cardValue: string
  phrases: PhrasePointObject[]
  newLocation?: DataPointLocation
  isSapMode?: boolean
  source?: 'dataobject' | 'approval'
  isSelected?: boolean
  onUpdated?: (item: DataObjectData) => void
  onUpdatedStatus?: (item: DataObjectData) => void
  onItemDeselected?: () => void
}

export interface SaveItemConfig {
  dataObject: DataObjectDTO
  item: DataObjectData
  fieldValue: string
  newFieldName?: string
  source?: 'dataobject' | 'approval'
  isSapMode?: boolean
  onUpdated?: (item: DataObjectData) => void
}

export interface DeleteItemConfig {
  dataObject: DataObjectDTO
  item: DataObjectData
  onDeleted?: (itemName: string) => void
}

export interface DataPointExternalControls {
  itemName: string
  isVisible: boolean
}

export interface SectionExternalControls {
  sectionId: string
  isVisible: boolean
}

export interface LoadDocumentConfig {
  createMissingPassportItems?: boolean
  deletedItem?: string
  force?: boolean
}

export enum FILTER_TYPES {
  All = 'All items',
  Open = 'Not approved',
  NoPhrasesFound = `With 'no phrases found'`,
}

export enum ApprovalModes {
  Default = 'Default',
  Advanced = 'Advanced',
  SAP = 'SAP',
  Compressed = 'Compressed',
}

export enum TabSectionsEnum {
  SAPTools = 'SAPTools',
  References = 'References',
  Comparison = 'Comparison',
}

export const APPROVAL_TIMESTAMP_ITEM_FORMAT = 'MM.DD.YYYY, HH:mm:ss'

export function formatCanonicalByCanonicalPreffixAndNumber(
  canonicalSectionPrefix: string,
  canonicalPreffix: string,
  suffixNumber: number,
): string {
  return `${canonicalSectionPrefix}.${canonicalPreffix}_${suffixNumber}`
}

export function getNewDefaultItem(itemName?: string): DataObjectData {
  return {
    fieldName: itemName || '',
    fieldValue: '',
    confidence: 0,
    location: [],
    action: CanonicalAction.Extracted,
    props: [],
  }
}

export function getDefaultProp(data: Partial<Props>): Props {
  return {
    sort: data.sort || 0,
    sequence: data.sequence || 0,
    source: data.source || '',
    sourceNote: data.sourceNote || '',
    recordNo: data.recordNo || '',
    isReferenced: data.isReferenced || false,
    isInherited: data.isInherited || false,
    usages: data.usages || [],
    propItems: data.propItems || [],
  }
}

export function getNewDefaultPropItem(data: Partial<PropItems>): PropItems {
  return {
    subCanonicalName: data.subCanonicalName || '',
    sort: data.sort || 1,
    propName: data.propName || '',
    propDesc: data.propDesc || '',
    propPhraseKey: data.propPhraseKey || '',
    propPhraseLib: data.propPhraseLib || '',
    subCharCategory: data.subCharCategory || '',
    confidence: data.confidence || 100,
    propTitle: data.propTitle,
    propPhraseCode: data.propPhraseCode || '',
    propPhraseGroup: data.propPhraseGroup || '',
    isPhrase: data.isPhrase || false,
    propValue: data.propValue || '',
  }
}

export function getNewDefaultUsage(value?: string): Usages {
  return {
    rating: 'PUBLIC',
    excl_ind: false,
    validity_area: value || '',
    act_ind: true,
    rel_ind: false,
  }
}

export function isClassificationItem(fieldName: string) {
  return SDS_CLASSIFICATION_CANONICAL_LIST.find(
    (i) => fieldName && i === getCanonicalNameWithoutNumberSuffix(fieldName),
  )
}

export default function useApprovals(
  documentComposable: ReturnType<typeof useDocuments>,
  phrasesComposable: UnwrapNestedRefs<ReturnType<typeof usePhrases>>,
  canonicalsConfig: Ref<CanonicalsConfigObject[] | undefined>,
) {
  const { updateOrCreateItemFromDataObject } = useDataObjects()
  const { t } = useI18n()
  const { addToast, addDefaultToast } = useToasts()
  const { formatActivityLogToStoreInApprovalMetadata } = useApprovalHistory()
  const { Permissions, hasPermission } = useRolesAndPermissions()
  const isVeryfing = ref(false)
  const verifyingCanonical = ref<string | undefined>()
  const savingCanonical = ref<string | undefined>()
  const deletingCanonical = ref<string | undefined>()
  const isAnyItemActionInProgress = computed(
    () => !!(verifyingCanonical.value || savingCanonical.value || deletingCanonical.value),
  )
  const currentApprovalTab = ref<TabSectionsEnum>(TabSectionsEnum.SAPTools)
  const approvalGeneralTabsSections = [
    {
      label: t('SAP Tools'),
      id: TabSectionsEnum.SAPTools,
    },
    { label: t('References'), id: TabSectionsEnum.References },
    { label: t('Comparison'), id: TabSectionsEnum.Comparison },
  ].filter((section) => {
    if (
      window.ENV.ENABLE_REFERENCES_TAB_IN_APPROVALS &&
      section.id === TabSectionsEnum.References &&
      hasPermission(Permissions.AddReferencesToMaterials)
    ) {
      return true
    }

    if (window.ENV.ENABLE_COMPARE_MATERIALS_IN_APPROVALS && section.id === TabSectionsEnum.Comparison) {
      return true
    }

    if (section.id === TabSectionsEnum.SAPTools) {
      return true
    }

    return false
  })

  // We only return the value 'true' in the event that a validation fails, and we should not display the failure request message.
  async function toggleItemProvenance(config: ToggleConfig) {
    if (!config.dataObject.dataAssetsId || !config.dataObject.id) {
      throw new Error()
    }

    isVeryfing.value = true
    verifyingCanonical.value = config.item.fieldName

    const isItemValidated = config.item?.action === CanonicalAction.Verified

    // TODO: Emit event to change phrases mode to false
    // if (!props.isSapMode) changePhrasesMode.value = false
    try {
      const newItemData: DataObjectData = config.item
        ? { ...config.item }
        : {
            fieldName: '',
            fieldValue: '',
            action: CanonicalAction.Extracted,
          }
      newItemData.action = isItemValidated ? CanonicalAction.Extracted : CanonicalAction.Verified

      applyAIRulesToTheConfidenceField(isItemValidated, newItemData, config.cardValue)

      if (config.newLocation && config.newLocation.page && config.newLocation.bounds)
        newItemData.location = [config.newLocation]
      if (config.item.fieldValue !== config.cardValue) newItemData.fieldValue = config.cardValue

      await approvalsApi.createOrUpdateItemsInApproval({
        dataAssetId: config.dataObject.dataAssetsId,
        dataObjectId: config.dataObject.id,
        dataObjectData: [newItemData],
      })
      config.item.action = newItemData.action

      if (documentComposable) {
        documentComposable.storeMetadata({
          newActivityLog: formatActivityLogToStoreInApprovalMetadata(ActivityActions.Update, [
            {
              object: config.item,
              objectChanges: newItemData,
            },
          ]),
        })
      }

      if (config.isSapMode) {
        afterSave(config.item, config.cardValue, config.phrases)
      }

      afterSaveForSpecialItems(config.item, config.cardValue)

      // TODO: Emit event
      // emits('updated:item')
      addToast({ text: t('Item status changed'), variant: 'success' })
    } catch {
      throw new Error()
    }

    verifyingCanonical.value = undefined
    isVeryfing.value = false
  }

  // Extended version of toggleItemProvenance for the item cards: supports both data sources, runs the number
  // validation, and reports the result through callbacks so the calling component can emit its events.
  async function toggleItemStatus(config: ToggleItemStatusConfig) {
    if (!config.dataObject.dataAssetsId || !config.dataObject.id) {
      throw new Error()
    }

    isVeryfing.value = true
    verifyingCanonical.value = config.item.fieldName

    const isItemValidated = config.item?.action === CanonicalAction.Verified

    try {
      await beforeSave(config.item, isItemValidated)

      const newItemData: DataObjectData = config.item
        ? { ...config.item }
        : {
            fieldName: '',
            fieldValue: '',
            action: CanonicalAction.Extracted,
          }
      newItemData.action = isItemValidated ? CanonicalAction.Extracted : CanonicalAction.Verified

      applyAIRulesToTheConfidenceField(isItemValidated, newItemData, config.cardValue)

      if (config.newLocation && config.newLocation.page && config.newLocation.bounds)
        newItemData.location = [config.newLocation]
      if (config.item.fieldValue !== config.cardValue) newItemData.fieldValue = config.cardValue

      if (config.source === 'dataobject') {
        await updateOrCreateItemFromDataObject(config.dataObject, newItemData)
      } else {
        await approvalsApi.createOrUpdateItemsInApproval({
          dataAssetId: config.dataObject.dataAssetsId,
          dataObjectId: config.dataObject.id,
          dataObjectData: [newItemData],
        })
      }
      config.item.action = newItemData.action

      if (documentComposable) {
        documentComposable.storeMetadata({
          newActivityLog: formatActivityLogToStoreInApprovalMetadata(ActivityActions.Update, [
            {
              object: config.item,
              objectChanges: newItemData,
            },
          ]),
        })
      }

      if (config.isSapMode) {
        afterSave(config.item, config.cardValue, config.phrases)
      }

      afterSaveForSpecialItems(config.item, config.cardValue)

      config.onUpdated?.(newItemData)
      config.onUpdatedStatus?.(newItemData)

      // Only validated items can be selected to send to SAP, then, if the user had a validated item and changed the
      // status to extracted, we need to unselect it
      if (config.isSelected && !isItemValidated) {
        config.onItemDeselected?.()
      }

      addToast({ text: t('Item status changed'), variant: 'success' })
    } catch (error) {
      addToast({ text: t('Error changing item status'), variant: 'danger' })
      throw error
    } finally {
      verifyingCanonical.value = undefined
      isVeryfing.value = false
    }
  }

  async function beforeSave(item: DataObjectData, isItemValidated: boolean) {
    await phrasesComposable.validateInstanceItemsNumbers(
      [item],
      !isItemValidated ? t('The saved item has an invalid number in one of its instances') : '',
    )
  }

  async function saveItem(config: SaveItemConfig) {
    if (!config.dataObject.dataAssetsId || !config.dataObject.id) {
      return
    }

    const isItemValidated = config.item?.action === CanonicalAction.Verified
    if (isItemValidated) {
      return
    }

    savingCanonical.value = config.item.fieldName

    try {
      await beforeSave(config.item, isItemValidated)

      const newItemData: DataObjectData = {
        ...config.item,
        fieldValue: config.fieldValue,
        fieldName: config.newFieldName || config.item.fieldName,
      }

      if (config.source === 'dataobject') {
        await updateOrCreateItemFromDataObject(config.dataObject, newItemData)
      } else {
        await approvalsApi.createOrUpdateItemsInApproval({
          dataAssetId: config.dataObject.dataAssetsId,
          dataObjectId: config.dataObject.id,
          dataObjectData: [newItemData],
        })
      }

      const previousItem = config.dataObject.data?.find((i) => i.fieldName && i.fieldName === config.item.fieldName)

      if (documentComposable) {
        documentComposable.storeMetadata({
          newActivityLog: formatActivityLogToStoreInApprovalMetadata(ActivityActions.Update, [
            { object: previousItem || config.item, objectChanges: newItemData },
          ]),
        })
      }

      if (!config.isSapMode) {
        addToast({ text: t('New value saved!'), variant: 'success' })
      }

      config.onUpdated?.(newItemData)
    } catch {
      addDefaultToast('danger')
    }

    savingCanonical.value = undefined
  }

  async function deleteItem(config: DeleteItemConfig) {
    if (!config.dataObject.dataAssetsId || !config.dataObject.id || !config.item.fieldName) {
      return
    }

    deletingCanonical.value = config.item.fieldName

    try {
      await approvalsApi.deleteItemsInApproval({
        dataAssetId: config.dataObject.dataAssetsId,
        dataObjectId: config.dataObject.id,
        requestBody: [config.item.fieldName],
      })

      if (documentComposable) {
        documentComposable.storeMetadata({
          newActivityLog: formatActivityLogToStoreInApprovalMetadata(ActivityActions.Delete, [{ object: config.item }]),
        })
      }

      config.onDeleted?.(config.item.fieldName)
      addToast({ text: t('Item deleted'), variant: 'success' })
    } catch {
      addDefaultToast('danger')
    }

    deletingCanonical.value = undefined
  }

  // We need to use the local phrases, for this reason we don't use the phrases of the phrases composable
  async function afterSave(item: DataObjectData, cardValue: string, phrases: PhrasePointObject[]) {
    const isItemWithSinglePhraseForSAP = phrasesComposable?.isItemWithSinglePhraseForSAP(item.fieldName || '') ?? true

    if (isItemWithSinglePhraseForSAP && phrases.length > 0 && cardValue) {
      // These items has not phrases logic, but we need to save the text in the single phrase item because that's used for SAP
      phrases[0].phrase_txt = cardValue
    }

    phrasesComposable?.updateItemPhrases(item.fieldName || '', phrases)
  }

  // Special items like 'Name' or 'CAS' need to update the phrase value in order to keep update the latest value in the SAP pages
  async function afterSaveForSpecialItems(item: DataObjectData, cardValue: string) {
    const isItemWithSinglePhraseForSAP = phrasesComposable?.isItemWithSinglePhraseForSAP(item.fieldName || '') ?? true
    const currentItemConfig = getCurrentItemConfig(item.fieldName || '')
    const phrases = phrasesComposable?.getPhrasesForItem(item.fieldName || '') || []

    const isSAPItem = currentItemConfig?.flag_sap === 1 || currentItemConfig?.flag_sap === 1.0
    const needUpdatePhraseValue = currentItemConfig?.flag_lit_props === 0 || currentItemConfig?.flag_lit_props === 0.0

    if (isItemWithSinglePhraseForSAP && phrases.length > 0 && isSAPItem && needUpdatePhraseValue && item.fieldName) {
      // These items has not phrases logic, but we need to save the text in the single phrase item because that's used for SAP
      phrases[0].phrase_txt = cardValue
      // We need to update also the content_subset
      phrases[0].content_subset = cardValue
      phrasesComposable?.updateItemPhrases(item.fieldName, phrases)
    }
  }

  function getCurrentItemConfig(canonicalName: string) {
    return (canonicalsConfig.value || []).find((c) => c.canonical_name === canonicalName)
  }

  return {
    isVeryfing,
    verifyingCanonical,
    savingCanonical,
    deletingCanonical,
    isAnyItemActionInProgress,
    currentApprovalTab,
    approvalGeneralTabsSections,
    toggleItemProvenance,
    toggleItemStatus,
    saveItem,
    deleteItem,
  }
}
